import React, { memo, forwardRef } from "react"
import PropTypes from "prop-types"
import { Button } from "reactstrap"
import Datepicker from "./Datepicker"
import Icon from "components/common/Icon"
import IconWithBg from "ui/IconWithBg"
import { DATE_FORMAT } from "modules/datepicker/constants"
import moment from "moment"

DateSwitcher.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
  type: PropTypes.string,
  format: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  isCompleteDateRange: PropTypes.bool
}
function DateSwitcher(
  { value, type = "date", format: externalFormat = DATE_FORMAT, onChange, className, isCompleteDateRange = false, ...rest },
  ref
) {
  const classes = ["hstack align-items-stretch white-space-nowrap"]
  if (className) classes.push(className)

  return (
    <Datepicker
      {...rest}
      wrapperTag="div"
      wrapperProps={{ className: classes.join(" ") }}
      tag={PickerButton}
      decrementTag={DecrementButton}
      incrementTag={IncrementButton}
      type={type}
      name={rest.name || type}
      value={value}
      onChange={onChange}
      format={externalFormat}
      display={{ ...rest.display, buttons: { today: true } }}
      ref={ref}
      isCompleteDateRange={isCompleteDateRange}
    />
  )
}

const getMonthRangeLabel = (date) => {
  if (!date) return ""

  // 1. Get the start and end of the current month
  const monthStartDate = moment(date).startOf("month") // e.g., Oct 1, 2025
  const monthEndDate = moment(date).endOf("month") // e.g., Oct 31, 2025

  // 2. Determine the full visible grid end date (which is the end of the month's week)
  const gridEndDate = monthEndDate.clone().endOf("week") // e.g., Sat, Nov 1, 2025

  // 3. Format the current month's label
  const startLabel = monthStartDate.format("MMMM YYYY") // e.g., "October 2025"

  // 4. Check if the end of the full grid is in the next month
  if (gridEndDate.isAfter(monthEndDate, "month")) {
    // Check if the year has also changed (e.g., Dec -> Jan)
    if (gridEndDate.isAfter(monthEndDate, "year")) {
      // e.g., "December 2025 - January 2026"
      const currentMonth = monthStartDate.format("MMMM YYYY")
      const nextMonth = gridEndDate.format("MMMM YYYY")
      return `${currentMonth} - ${nextMonth}`
    }

    // If only the month has changed (e.g., Oct -> Nov in the same year)
    // e.g., "October - November 2025"
    const currentMonthName = monthStartDate.format("MMMM")
    const nextMonthName = gridEndDate.format("MMMM")
    const year = monthStartDate.format("YYYY")
    return `${currentMonthName} - ${nextMonthName} ${year}`
  }

  // 5. If the grid ends within the current month, return the standard label
  return startLabel // e.g., "October 2025"
}

/**
 * Get week range label in format: "Aug 19-25, 2025"
 * Handles weeks spanning across months/years
 * Uses isoWeek (Monday-Sunday) to match calendar grid
 * @param {Date|moment} date - The date within the week
 * @returns {string} - Formatted week range label
 */
const getWeekRangeLabel = (date) => {
  if (!date) return ""

  // Use day(0)/day(6) to force Sunday–Saturday regardless of locale
  const weekStart = moment(date).clone().day(0).startOf("day")
  const weekEnd = moment(date).clone().day(6).startOf("day")

  const startMonth = weekStart.format("MMM") // Short month name (Jan, Feb, etc.)
  const endMonth = weekEnd.format("MMM")
  const startDay = weekStart.format("D")
  const endDay = weekEnd.format("D")
  const startYear = weekStart.format("YYYY")
  const endYear = weekEnd.format("YYYY")

  // If week spans across years
  if (startYear !== endYear) {
    // Format: "Dec 28, 2024 - Jan 3, 2025"
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`
  }

  // If week spans across months (same year)
  if (startMonth !== endMonth) {
    // Format: "Dec 28 - Jan 3, 2025"
    return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${endYear}`
  }

  // If week is within same month
  // Format: "Aug 19-25, 2025"
  return `${startMonth} ${startDay}-${endDay}, ${endYear}`
}

const PickerButton = memo(
  forwardRef(({ date, displayValue, valid, type, children, isCompleteDateRange, ...rest }, ref) => {
    let label = displayValue

    // Use custom week range label for week view
    if (type === "week" && date) {
      label = getWeekRangeLabel(date)
    }

    return (
      <Button {...rest} className="btn-ghost px-2 py-2 rounded-1" innerRef={ref}>
        <span className={["h3 lh-1 d-block mb-n1", valid ? "" : "opacity-50"].join(" ")}>{label}</span>
      </Button>
    )
  })
)

PickerButton.propTypes = {
  date: PropTypes.instanceOf(Date),
  type: PropTypes.string,
  displayValue: PropTypes.string.isRequired,
  valid: PropTypes.bool.isRequired,
  isCompleteDateRange: PropTypes.bool,
  children: PropTypes.node
}

const DecrementButton = memo(
  forwardRef((props, ref) => (
    <Button className="btn-ghost text-dark px-2 py-2 rounded-1" {...props} innerRef={ref} type="button">
      <IconWithBg icon={<Icon iconName="SidebarToggle" className="rotate-90" size={18} block />} />
    </Button>
  ))
)

const IncrementButton = memo(
  forwardRef((props, ref) => (
    <Button className="btn-ghost text-dark px-2 py-2 rounded-1" {...props} innerRef={ref} type="button">
      <IconWithBg icon={<Icon iconName="SidebarToggle" className="rotate-270" size={18} block />} />
      {/* <Icon iconName="SidebarToggle" className="rotate-270" size={18} block /> */}
    </Button>
  ))
)

export default memo(forwardRef(DateSwitcher))
